import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  WebRecorder,
  detectWebCapabilities,
  formatBytes,
  formatDuration,
  openExternalSink,
  type RecorderCapabilities,
  type RecordingEntry,
  type RecordingSink,
} from "@hybrid-iptv/recorder";
import { runsOnDevice } from "../api/client.js";

/**
 * Recording, as the player UI sees it (Section 15 step 2).
 *
 * Live only. VOD is already a file on the provider's server, so "recording" it
 * would mean re-downloading something the user can already resume — the
 * interesting case is live TV, which is gone once it has aired.
 *
 * The engine is created lazily, per recording: constructing a WebRecorder is
 * cheap, and holding one across source changes would leave it bound to a video
 * element whose stream has been replaced.
 */

/** Rough live bitrate for the "you can record about X" estimate. Measured on
 * this deployment: a 1080p provider stream sits near 5 Mbps. It only drives an
 * estimate, so being approximate is fine — but it is named rather than buried
 * so a future measurement can correct it. */
const ASSUMED_LIVE_BITRATE_BPS = 5_000_000;

export interface RecordingUiState {
  /** Show the control at all. False on browsers that cannot capture. */
  supported: boolean;
  /** Why it is unavailable, when it is. */
  unavailableReason?: string;
  capabilities: RecorderCapabilities;
  recording: boolean;
  entry: RecordingEntry | null;
  /** Human-readable size so far, e.g. "412 MB". */
  sizeLabel: string | null;
  /** "about 1 h 26 min" — what the device can still hold, or null if unknown. */
  headroomLabel: string | null;
  /** Set after a failed or stopped-early recording; cleared on the next start. */
  message: string | null;
  start: (options?: { external?: boolean; sink?: RecordingSink; stopAtMs?: number; title?: string }) => Promise<void>;
  stop: () => Promise<void>;
  dismissMessage: () => void;
}

export function useRecording(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  source: { title?: string; type?: string } | null,
  contentRef: string | null,
): RecordingUiState {
  const capabilities = useMemo(() => detectWebCapabilities(), []);
  const recorderRef = useRef<WebRecorder | null>(null);
  const [entry, setEntry] = useState<RecordingEntry | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [headroomLabel, setHeadroomLabel] = useState<string | null>(null);

  const isLive = source?.type === "live";
  // Not in the Android app: its picture comes from the native player, so the
  // page's <video> has nothing to capture. The app records with its own DVR.
  const supported = capabilities.canRecord && isLive && !runsOnDevice();
  const stopTimerRef = useRef<number | null>(null);

  // Refresh the headroom estimate when the control becomes relevant. Shown
  // BEFORE recording so the limit is visible up front rather than discovered
  // when a file stops mid-programme.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    const recorder = new WebRecorder({ video: videoRef.current ?? document.createElement("video") });
    void recorder
      .storage()
      .then((space) => {
        if (cancelled) return;
        const seconds = space.estimatedSecondsAt(ASSUMED_LIVE_BITRATE_BPS);
        setHeadroomLabel(seconds === undefined ? null : `about ${formatDuration(seconds)}`);
      })
      .catch(() => undefined)
      .finally(() => recorder.dispose());
    return () => {
      cancelled = true;
    };
  }, [supported, videoRef]);

  useEffect(
    () => () => {
      recorderRef.current?.dispose();
      recorderRef.current = null;
    },
    [],
  );

  const start = useCallback(
    async (options?: { external?: boolean; sink?: RecordingSink; stopAtMs?: number; title?: string }) => {
      const video = videoRef.current;
      if (!video || !contentRef) return;
      setMessage(null);
      const recorder = new WebRecorder({
        video,
        // External writes go to a file the user picks, which may be an attached
        // drive — the only route out of the browser quota. Desktop only. The
        // guide's "Record now" picks the file itself (it had the click) and
        // hands it over here.
        ...(options?.sink
          ? { openSink: () => Promise.resolve(options.sink!) }
          : options?.external
            ? { openSink: openExternalSink }
            : {}),
      });
      if (stopTimerRef.current !== null) window.clearTimeout(stopTimerRef.current);
      if (options?.stopAtMs) {
        // Stop at the programme's end. A timer this long is fine in a visible tab;
        // a hidden tab may run it late, which only makes the file a little longer.
        stopTimerRef.current = window.setTimeout(() => {
          stopTimerRef.current = null;
          void recorder.stop().catch(() => undefined);
        }, Math.max(0, options.stopAtMs - Date.now()));
      }
      recorderRef.current = recorder;
      recorder.on("statechange", (next) => setEntry(next));
      recorder.on("progress", (next) => setEntry(next));
      recorder.on("error", (error) => setMessage(error.message));
      try {
        await recorder.start({
          contentRef,
          // The guide passes the programme's title; the player's own button has only the channel.
          title: options?.title ?? source?.title ?? "Recording",
          ...(source?.title ? { channelName: source.title } : {}),
        });
      } catch (error) {
        // start() rejects with a RecorderError whose message is already written
        // for a person — the storage-limited case names the free space.
        const text =
          typeof error === "object" && error && "message" in error
            ? String((error as { message: unknown }).message)
            : "Could not start recording.";
        setMessage(text);
        recorder.dispose();
        recorderRef.current = null;
      }
    },
    [contentRef, source?.title, videoRef],
  );

  const stop = useCallback(async () => {
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    const recorder = recorderRef.current;
    if (!recorder) return;
    await recorder.stop().catch(() => undefined);
    recorder.dispose();
    recorderRef.current = null;
  }, []);

  const recording = entry?.status === "recording" || entry?.status === "starting";

  return {
    supported,
    ...(capabilities.unavailableReason ? { unavailableReason: capabilities.unavailableReason } : {}),
    capabilities,
    recording,
    entry,
    sizeLabel: entry && entry.bytes > 0 ? formatBytes(entry.bytes) : null,
    headroomLabel,
    message,
    start,
    stop,
    dismissMessage: () => setMessage(null),
  };
}
